/**
 * TabRenderer — extracts the 16+ conditional tab branches from App.tsx.
 * Each tab is rendered inside a shared TabWrapper (except LiveStudioTab and output).
 * Heavy tabs are lazy-loaded via React.lazy + Suspense + ErrorBoundary.
 */

import React, { useState, useMemo } from 'react';
import { OutputSection } from '@components/OutputSection';
import { SunoReadinessPanel } from '@features/SunoReadiness';
import { PromptDoctorPanel } from '@/features/PromptDoctor/components/PromptDoctorPanel';
import { blendsInStyle } from '@/features/PromptDoctor/blendsInStyle';
import { SunoConfigTab } from '@components/SunoConfigTab';
import { ImageConfigTab } from '@components/ImageConfigTab';
import { StudioTeamPanel } from '@components/StudioTeam/StudioTeamPanel';
import { QuickModeStudio } from '@features/ModeSystem';
import { VariationChainPanel } from '@components/VariationChains/VariationChainPanel';
import { Button } from '@components/ui';
import { useVocalPacks } from '@hooks/useVocalPacks';
import { resolveStyleTags } from '@services/llmShared';
import { Activity, Music, AlertCircle, CheckCircle2, GitBranch, LayoutGrid, RefreshCw, Sliders, Users } from 'lucide-react';
import { LyricOrchestratorPanel } from '@components/LyricOrchestrator/LyricOrchestratorPanel';
import { LyricPolisherPanel } from '@components/LyricPolisher/LyricPolisherPanel';
import {
  LazyTabWrapper,
  LazyLiveStudioTab,
  LazySunoHistoryTab,
  LazyHHHistoryTab,
  LazyMusicCriticTab,
  LazyVocalBlender,
  LazyProductionIntelligence,
  LazyAlbumModeContainer,
  LazyRiffWriterTab,
  LazyNarrativeEngineTab,
  LazySessionHistoryTab,
  LazyMidiForgeTab,
  LazyMasterForgeTab,
  LazyAnalyticsTab,
  LazySuiteTab,
  LazyAxeLabTab,
  LazyRouletteTab,
  LazySimpleModeTab,
  LazyStudioRouletteTab,
  LazySpeechTab,
  LazyManualEntryTab,
} from '@components/LazyTabs';
import type { HookhouseFormData, ParsedSunoOutput, SunoClip, MusicCritique } from '@/types';
import type { HookHouseTemplate, TemplateConfig } from '@/types/template.types';
import type { SongSeed } from '@components/RiffWriter/types';
import type { SituationCard } from '@hooks/useStoryEngine';
import type { SpawnStrategy, VariationNode, VariationChain } from '@/types/variationChain.types';
import type { Persona } from '@/types/persona.types';
import type { LyricDeliberationResult, LyricDeliberationStatus, LyricProgressEvent, PolishResult, PolishStatus, PolishProgressEvent } from '@/types/lyricOrchestrator.types';
import { PersonaPicker } from '@components/Persona/PersonaPicker';
import { useAuth } from '@/context/AuthContext';

// ─── Shared tab wrapper ─────────────────────────────────────

const TabWrapper: React.FC<{ children: React.ReactNode; tabId: string }> = ({ children, tabId }) => (
  <div
    id={`tabpanel-${tabId}`}
    role="tabpanel"
    aria-labelledby={`tab-${tabId}`}
    className="bg-slate-900/50 backdrop-blur-sm rounded-2xl border-l-4 border-l-accent p-10 shadow-2xl"
  >
    {children}
  </div>
);

// ─── Props ──────────────────────────────────────────────────

export interface TabRendererProps {
  activeTab: string;
  onTabChange: (tab: string) => void;

  // Generation state
  isGenerating: boolean;
  output: ParsedSunoOutput | null;
  error: string | null;
  currentBlueprint: string | null;
  currentCritique: MusicCritique | null;

  // Studio config
  isQuickMode: boolean;
  formData: HookhouseFormData;
  currentBlend: string[];
  currentMood: string[];
  onMoodChange: (moods: string[]) => void;
  onBlendChange: (blends: string[]) => void;

  // Generation handlers
  onGenerate: (data: HookhouseFormData, vocalArch?: string) => Promise<void>;
  onAlbumGenerate: (data: any, vocalArch: string) => Promise<ParsedSunoOutput>;
  onRegenerateWithAdvice: () => Promise<void>;
  onLyricsUpdate: (lyrics: string) => Promise<void>;
  onOutputFieldUpdate: (field: 'summary' | 'style' | 'excludedStyle' | 'styleBlueprint', value: string) => Promise<void>;
  onCopyFromRiffWriter: (seed: SongSeed) => void;

  // Output handlers
  onSelectOutput: (output: ParsedSunoOutput) => void;
  onBlueprintGenerated: (blueprint: string | null) => void;
  onCritiqueGenerated: (critique: MusicCritique | null) => void;
  currentDraftId?: string | null;

  // Template/sharing
  onLoadTemplate: (template: HookHouseTemplate) => void;
  getCurrentConfig: () => TemplateConfig;
  onShareOpen: () => void;
  onSettingsOpen: () => void;
  onLibraryOpen: () => void;

  // Suno state
  sunoCookie: string;
  isSyncing: boolean;
  history: SunoClip[];
  onDirectSync: (data?: ParsedSunoOutput) => Promise<void>;
  openSunoPrep: (payload?: Partial<import('@components/SunoPrep/SunoPrepModal').SunoPrepPayload>) => void;
  fetchAndMergeSunoHistory: () => Promise<void>;

  // Narrative Engine
  narrativeSeed?: SongSeed | null;
  onBuildNarrative?: (seed: SongSeed) => void;
  onInjectNarrative?: (blueprint: any) => void;
  onSendNarrativeToStudioTeam?: (blueprint: any) => void;

  // Send-to-Studio-Team (from RiffWriter seed or Story Engine card)
  onSendToStudioTeam?: (seed: SongSeed) => void;
  onSendStoryToStudioTeam?: (card: SituationCard) => void;

  // Cross-feature seed routing (from RiffWriter "Send to ...")
  onSendToArt?: (seed: SongSeed) => void;
  onSendToAlbum?: (seed: SongSeed) => void;
  onSendToMidiForge?: (seed: SongSeed) => void;
  artSeed?: SongSeed | null;
  pendingAlbumTrackSeed?: SongSeed | null;
  onPendingAlbumTrackConsumed?: () => void;
  midiForgeIncomingVibe?: string | null;
  onMidiForgeVibeConsumed?: () => void;
  onSendInstrumentalToStudio?: (lyrics: string, title: string) => void;

  // Variation chains
  activeChain: VariationChain | null;
  getTree: () => VariationNode[];
  isChainLoading: boolean;
  onStartVariationChain: () => void;
  onSpawnVariations: (parentNodeId: string, count: number, strategy: SpawnStrategy) => Promise<void>;
  onRateVariationNode: (nodeId: string, rating: number) => void;
  onTriggerNodeGen: (node: VariationNode) => void;
  onDeleteChain: () => void;

  // Persona
  activePersona?: Persona | null;
  personas?: Persona[];
  activePersonaId?: string | null;
  onPersonaSelect?: (id: string | null) => void;
  onPersonaCreateNew?: () => void;
  onPersonaDelete?: (id: string) => void;
  onPersonaEdit?: (persona: Persona) => void;

  // Lyric Orchestrator (Parliament Write)
  onParliamentWrite?: (data: import('@/types').HookhouseFormData, vocalArchitecture?: string) => void;
  isParliamentWriting?: boolean;
  lyricOrchStatus?: LyricDeliberationStatus;
  lyricOrchResult?: LyricDeliberationResult | null;
  lyricOrchError?: string | null;
  lyricOrchProgress?: LyricProgressEvent | null;
  lyricOrchCompletedAgents?: string[];
  onLyricOrchCancel?: () => void;
  onLyricOrchReset?: () => void;
  onLyricOrchAccept?: () => void;

  // Lyric Polisher (Parliament Polish)
  onParliamentPolish?: (lyrics: string, data: import('@/types').HookhouseFormData, vocalArchitecture?: string) => void;
  isParliamentPolishing?: boolean;
  lyricPolishStatus?: PolishStatus;
  lyricPolishResult?: PolishResult | null;
  lyricPolishError?: string | null;
  lyricPolishProgress?: PolishProgressEvent | null;
  lyricPolishCompletedAgents?: string[];
  onLyricPolishCancel?: () => void;
  onLyricPolishReset?: () => void;
  onLyricPolishAccept?: () => void;
}

// ─── Component ──────────────────────────────────────────────

export const TabRenderer: React.FC<TabRendererProps> = (props) => {
  const { activeTab } = props;
  const { isAdmin } = useAuth();
  const { data: vocalPacks } = useVocalPacks();
  const voiceProfiles = useMemo(() =>
    Object.entries(vocalPacks).map(([key, pack]) => ({
      id: key,
      name: pack.name,
      description: pack.description,
      excludeStyles: pack.excludeStyles,
    })),
    [vocalPacks]
  );

  switch (activeTab) {
    case 'studio':
      return (
        <TabWrapper tabId="studio">
          <header className="text-center mb-8">
            <h2 className="text-3xl font-black text-slate-50 uppercase tracking-tighter flex items-center justify-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-accent text-slate-950 shadow-lg shadow-amber-900/20">
                <LayoutGrid size={20} />
              </div>
              Studio <span className="text-accent">Session</span>
            </h2>
            <p className="mt-2 text-slate-400 max-w-2xl mx-auto font-medium">
              Configure your vocal, production, and lyric settings for AI-powered music generation.
            </p>
          </header>
          {!props.isQuickMode && props.personas && props.onPersonaSelect && props.onPersonaCreateNew && props.onPersonaDelete && (
            <div className="mb-6">
              <PersonaPicker
                personas={props.personas}
                activePersonaId={props.activePersonaId || null}
                onSelect={props.onPersonaSelect}
                onCreateNew={props.onPersonaCreateNew}
                onDelete={props.onPersonaDelete}
                onEdit={props.onPersonaEdit}
              />
            </div>
          )}
          {props.isQuickMode ? (
            <QuickModeStudio onGenerate={props.onGenerate} isGenerating={props.isGenerating} />
          ) : (
            <SunoConfigTab
              onSubmit={props.onGenerate}
              isGenerating={props.isGenerating}
              onMoodChange={props.onMoodChange}
              onBlendChange={props.onBlendChange}
              onOpenBlender={() => props.onTabChange('vocals')}
              onOpenLibrary={props.onLibraryOpen}
              activeMoods={props.currentMood}
              activeBlends={props.currentBlend}
              onLoadTemplate={props.onLoadTemplate}
              getCurrentConfig={props.getCurrentConfig}
              personaOverride={props.activePersona ?? undefined}
              onParliamentWrite={props.onParliamentWrite}
              isParliamentWriting={props.isParliamentWriting}
              onParliamentPolish={props.onParliamentPolish}
              isParliamentPolishing={props.isParliamentPolishing}
            />
          )}
        </TabWrapper>
      );

    case 'vocals': {
      const vocalsLocked = !!props.activePersona || props.formData.stylePreset !== 'None';
      return (
        <TabWrapper tabId="vocals">
          {props.activePersona ? (
            <div className="mb-6 p-4 bg-amber-950/20 border border-amber-900/30 rounded-2xl text-center">
              <p className="text-[11px] font-black text-amber-400 uppercase tracking-widest">Persona Override Active: {props.activePersona.name}</p>
              <p className="text-[11px] text-slate-400 mt-1">Vocal architecture is provided by the active persona. Deactivate in Studio to re-enable.</p>
            </div>
          ) : props.formData.stylePreset !== 'None' ? (
            <div className="mb-6 p-4 bg-amber-950/20 border border-amber-900/30 rounded-2xl text-center">
              <p className="text-[11px] font-black text-amber-400 uppercase tracking-widest">Style Preset Override Active</p>
              <p className="text-[11px] text-slate-400 mt-1">Vocal architecture is bypassed. Change preset to "None" in Studio to re-enable.</p>
            </div>
          ) : null}
          <div className={vocalsLocked ? 'opacity-30 grayscale pointer-events-none' : ''}>
            <LazyTabWrapper>
              <LazyVocalBlender onNavigateToStudio={() => props.onTabChange('studio')} onShare={props.onShareOpen} />
            </LazyTabWrapper>
          </div>
        </TabWrapper>
      );
    }

    case 'production': {
      const prodLocked = !!props.activePersona || props.formData.stylePreset !== 'None';
      return (
        <TabWrapper tabId="production">
          {props.activePersona ? (
            <div className="mb-6 p-4 bg-amber-950/20 border border-amber-900/30 rounded-2xl text-center">
              <p className="text-[11px] font-black text-amber-400 uppercase tracking-widest">Persona Override Active: {props.activePersona.name}</p>
              <p className="text-[11px] text-slate-400 mt-1">Production architecture is provided by the active persona. Axe Lab remains active.</p>
            </div>
          ) : props.formData.stylePreset !== 'None' ? (
            <div className="mb-6 p-4 bg-amber-950/20 border border-amber-900/30 rounded-2xl text-center">
              <p className="text-[11px] font-black text-amber-400 uppercase tracking-widest">Style Preset Override Active</p>
              <p className="text-[11px] text-slate-400 mt-1">Production architecture is bypassed. Axe Lab remains active.</p>
            </div>
          ) : null}
          <LazyTabWrapper>
            <LazyProductionIntelligence
              musicalBlend={props.currentBlend}
              moodArchitecture={props.currentMood}
              onNavigateToStudio={() => props.onTabChange('studio')}
              onShare={props.onShareOpen}
              stylePresetOverride={prodLocked}
            />
          </LazyTabWrapper>
        </TabWrapper>
      );
    }

    case 'axelab':
      return (
        <TabWrapper tabId="axelab">
          <LazyTabWrapper>
            <LazyAxeLabTab />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'album':
      return (
        <TabWrapper tabId="album">
          <LazyTabWrapper>
            <LazyAlbumModeContainer
              voiceProfiles={voiceProfiles}
              onGeneratePrompt={props.onAlbumGenerate}
              onSyncToSuno={props.onDirectSync}
              isSunoReady={!!props.sunoCookie}
              onNavigateToBlender={() => props.onTabChange('vocals')}
              onNavigateToProduction={() => props.onTabChange('production')}
              incomingTrackSeed={props.pendingAlbumTrackSeed}
              onIncomingTrackConsumed={props.onPendingAlbumTrackConsumed}
            />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'live':
      return (
        <div id="tabpanel-live" role="tabpanel" aria-labelledby="tab-live">
          <LazyTabWrapper>
            <LazyLiveStudioTab />
          </LazyTabWrapper>
        </div>
      );

    case 'riffwriter':
      return (
        <TabWrapper tabId="riffwriter">
          <LazyTabWrapper>
            <LazyRiffWriterTab
              onCopyToStudio={props.onCopyFromRiffWriter}
              onBuildNarrative={props.onBuildNarrative}
              onSendToStudioTeam={props.onSendToStudioTeam}
              onSendToArt={props.onSendToArt}
              onSendToAlbum={props.onSendToAlbum}
              onSendToMidiForge={props.onSendToMidiForge}
              onSendStoryToStudioTeam={props.onSendStoryToStudioTeam}
              onShare={props.onShareOpen}
            />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'narrative':
      return (
        <TabWrapper tabId="narrative">
          <LazyTabWrapper>
            <LazyNarrativeEngineTab
              seed={props.narrativeSeed || null}
              onInjectToStudio={(bp) => props.onInjectNarrative?.(bp)}
              onSendToStudioTeam={props.onSendNarrativeToStudioTeam ? (bp) => props.onSendNarrativeToStudioTeam?.(bp) : undefined}
              onTabChange={props.onTabChange}
            />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'image':
      return (
        <TabWrapper tabId="image">
          <ImageConfigTab
            output={props.output}
            blueprint={props.currentBlueprint}
            onBlueprintGenerated={props.onBlueprintGenerated}
            draftId={props.currentDraftId}
            artSeed={props.artSeed}
          />
        </TabWrapper>
      );

    case 'studio_team':
      return (
        <TabWrapper tabId="studio_team">
          <StudioTeamPanel openSunoPrep={props.openSunoPrep} activePersona={props.activePersona} />
        </TabWrapper>
      );

    case 'roulette':
      return (
        <TabWrapper tabId="roulette">
          <LazyTabWrapper>
            <LazyRouletteTab openSunoPrep={props.openSunoPrep} />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'simplemode':
      return (
        <TabWrapper tabId="simplemode">
          <LazyTabWrapper>
            <LazySimpleModeTab />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'studioroulette':
      return (
        <TabWrapper tabId="studioroulette">
          <LazyTabWrapper>
            <LazyStudioRouletteTab
              onMoodChange={props.onMoodChange}
              onBlendChange={props.onBlendChange}
              onTabChange={props.onTabChange}
            />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'speech':
      return (
        <TabWrapper tabId="speech">
          <LazyTabWrapper>
            <LazySpeechTab />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'critic':
      return (
        <TabWrapper tabId="critic">
          <LazyTabWrapper>
            <LazyMusicCriticTab
              currentCritique={props.currentCritique}
              onCritiqueGenerated={props.onCritiqueGenerated}
              onSelectOutput={props.onSelectOutput}
              onRevise={props.onRegenerateWithAdvice}
              isGeneratingRevision={props.isGenerating}
            />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'suno_hist':
      return (
        <TabWrapper tabId="suno_hist">
          <LazyTabWrapper>
            <LazySunoHistoryTab
              history={props.history}
              isSyncing={props.isSyncing}
              onSync={props.fetchAndMergeSunoHistory}
              sunoCookie={props.sunoCookie}
              onTabChange={props.onTabChange}
            />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'hh_hist':
      return (
        <TabWrapper tabId="hh_hist">
          <LazyTabWrapper>
            <LazyHHHistoryTab
              onSelect={(out) => {
                props.onSelectOutput(out);
                props.onTabChange('output');
              }}
              onSendToSuno={props.openSunoPrep}
            />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'manual_entry':
      return (
        <TabWrapper tabId="manual_entry">
          <LazyTabWrapper>
            <LazyManualEntryTab />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'sessions':
      return (
        <TabWrapper tabId="sessions">
          <LazyTabWrapper>
            <LazySessionHistoryTab />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'midiforge':
      return (
        <TabWrapper tabId="midiforge">
          <LazyTabWrapper>
            <LazyMidiForgeTab
              onMasterThis={(jobId) => {
                localStorage.setItem('masterforge_from_midiforge', jobId);
                props.onTabChange('masterforge');
              }}
              incomingVibe={props.midiForgeIncomingVibe}
              onIncomingVibeConsumed={props.onMidiForgeVibeConsumed}
              onSendInstrumentalToStudio={props.onSendInstrumentalToStudio}
            />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'masterforge':
      return (
        <TabWrapper tabId="masterforge">
          <LazyTabWrapper>
            <LazyMasterForgeTab
              currentDraft={props.output ? { title: props.output.title, lyrics: props.output.lyricsWithTags, style: resolveStyleTags(props.output), styleStrength: props.output.styleStrength, weirdness: props.output.weirdness } : null}
            />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'analytics':
      if (!isAdmin) {
        return (
          <TabWrapper tabId="analytics">
            <div className="p-12 text-center text-slate-400">
              <p className="text-sm">Analytics is admin-only.</p>
            </div>
          </TabWrapper>
        );
      }
      return (
        <TabWrapper tabId="analytics">
          <LazyTabWrapper>
            <LazyAnalyticsTab />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'suite':
      return (
        <TabWrapper tabId="suite">
          <LazyTabWrapper>
            <LazySuiteTab />
          </LazyTabWrapper>
        </TabWrapper>
      );

    case 'output':
      return <OutputTab {...props} />;

    default:
      return null;
  }
};

// ─── Output tab (complex enough to warrant its own component) ─

const OutputTab: React.FC<TabRendererProps> = (props) => {
  const [showTweakPanel, setShowTweakPanel] = useState(false);
  const [tweakWeirdness, setTweakWeirdness] = useState(0);
  const [tweakStyleStrength, setTweakStyleStrength] = useState(0);

  return (
    <div id="tabpanel-output" role="tabpanel" aria-labelledby="tab-output" className="w-full">
      <header className="text-center mb-8">
        <h2 className="text-3xl font-black text-slate-50 uppercase tracking-tighter flex items-center justify-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-accent text-slate-950 shadow-lg shadow-amber-900/20">
            <CheckCircle2 size={20} />
          </div>
          Session <span className="text-accent">Output</span>
        </h2>
        <p className="mt-2 text-slate-400 max-w-2xl mx-auto font-medium">
          Review your generated session architecture, sync to Suno, and evolve variations.
        </p>
      </header>

      {/* Parliament Write panel — shown when active or has results */}
      {(props.lyricOrchStatus && props.lyricOrchStatus !== 'idle') && (
        <div className="mb-6">
          <LyricOrchestratorPanel
            status={props.lyricOrchStatus}
            isWriting={props.isParliamentWriting || false}
            result={props.lyricOrchResult || null}
            error={props.lyricOrchError || null}
            progress={props.lyricOrchProgress || null}
            completedAgents={props.lyricOrchCompletedAgents || []}
            onCancel={props.onLyricOrchCancel || (() => {})}
            onReset={props.onLyricOrchReset || (() => {})}
            onAccept={props.onLyricOrchAccept || (() => {})}
          />
        </div>
      )}

      {/* Parliament Polish panel — shown when active or has results */}
      {(props.lyricPolishStatus && props.lyricPolishStatus !== 'idle') && (
        <div className="mb-6">
          <LyricPolisherPanel
            status={props.lyricPolishStatus}
            isPolishing={props.isParliamentPolishing || false}
            result={props.lyricPolishResult || null}
            error={props.lyricPolishError || null}
            progress={props.lyricPolishProgress || null}
            completedAgents={props.lyricPolishCompletedAgents || []}
            onCancel={props.onLyricPolishCancel || (() => {})}
            onReset={props.onLyricPolishReset || (() => {})}
            onAccept={props.onLyricPolishAccept || (() => {})}
          />
        </div>
      )}

      {props.error && (
        <div className="bg-red-700/20 border border-red-700 text-red-400 p-6 rounded-xl mb-8 flex items-start gap-4 backdrop-blur-md animate-in slide-in-from-top-4">
          <AlertCircle className="flex-shrink-0 mt-0.5" size={24} />
          <div>
            <h3 className="font-bold text-lg uppercase tracking-tight">System Architect Error</h3>
            <p className="mt-1 opacity-90">{props.error}</p>
            <button onClick={props.onSettingsOpen} className="mt-4 px-4 py-2 bg-red-700 text-white rounded font-bold text-xs uppercase tracking-widest hover:bg-red-600 transition-colors">Resolve in Settings</button>
            <button onClick={() => props.onTabChange('studio')} className="mt-4 ml-3 px-4 py-2 bg-slate-800 text-slate-300 rounded font-bold text-xs uppercase tracking-widest">Return to Studio</button>
          </div>
        </div>
      )}

      {!props.output && !props.isGenerating && !props.error && (
        <div className="h-[500px] flex flex-col items-center justify-center text-slate-400 border-2 border-dashed border-slate-800 rounded-3xl p-12 text-center backdrop-blur-sm bg-slate-950/30">
          <div className="p-8 bg-slate-900/50 rounded-full mb-6 border border-slate-800">
            <Music size={64} className="opacity-20" />
          </div>
          <h3 className="text-2xl font-black text-slate-400 uppercase tracking-widest">Awaiting Architecture</h3>
          <p className="max-w-md mt-4 text-slate-400 leading-relaxed">
            The output vault is currently empty. Head to the <strong>Studio</strong> or <strong>Live</strong> tabs to synthesize a new session blueprint.
          </p>
        </div>
      )}

      {props.isGenerating && (
        <div className="h-[500px] flex flex-col items-center justify-center space-y-8 py-20 bg-slate-900/40 rounded-3xl border-l-4 border-l-accent shadow-2xl">
          <div className="relative">
            <div className="w-24 h-24 border-4 border-accent/20 border-t-accent animate-spin rounded-full shadow-[0_0_30px_rgba(192, 93, 62,0.3)]" />
            <Activity size={36} className="absolute inset-0 m-auto text-accent animate-pulse" />
          </div>
          <div className="text-center space-y-3">
            <h3 className="text-xl font-black text-slate-200 uppercase tracking-tighter">Forging Session Data</h3>
            <p className="font-mono text-[11px] animate-pulse text-accent uppercase tracking-[0.4em]">
              Synchronizing with Session Architect...
            </p>
          </div>
        </div>
      )}

      {props.output && !props.isGenerating && (
        <div className="space-y-6">
          {/* Suno Readiness Analyzer */}
          <SunoReadinessPanel
            styleText={resolveStyleTags(props.output)}
            lyricsText={props.output.lyricsWithTags || ''}
            blends={blendsInStyle(resolveStyleTags(props.output), props.formData.blends)}
          />

          {/* Prompt Doctor */}
          <PromptDoctorPanel
            styleText={resolveStyleTags(props.output)}
            lyricsText={props.output.lyricsWithTags || ''}
            blends={blendsInStyle(resolveStyleTags(props.output), props.formData.blends)}
            openSunoPrep={props.openSunoPrep}
          />

          <div className="bg-slate-900/40 p-2 sm:p-8 rounded-3xl border-l-4 border-l-accent shadow-2xl">
            <OutputSection
              output={props.output}
              blueprint={props.currentBlueprint}
              critique={props.currentCritique}
              isSyncing={props.isSyncing}
              onSync={() => {
                const o = props.output!;
                props.openSunoPrep({
                  title: o.title || '',
                  tags: resolveStyleTags(o),
                  negativeTags: o.excludedStyle || '',
                  lyrics: o.lyricsWithTags || '',
                  styleStrength: o.styleStrength ?? 85,
                  weirdness: o.weirdness ?? 65,
                  makeInstrumental: !o.lyricsAlone,
                  draftId: props.currentDraftId ?? null,
                });
              }}
              onApplyRevision={props.onRegenerateWithAdvice}
              onLyricsUpdate={props.onLyricsUpdate}
              onFieldUpdate={props.onOutputFieldUpdate}
              editingEnabled={!!props.currentDraftId}
              isSunoReady={!!props.sunoCookie}
            />
          </div>

          {/* Quick Regenerate */}
          <div className="flex items-center justify-center gap-3 pt-4">
            <Button variant="secondary" size="sm" leftIcon={<RefreshCw size={14} />}
              onClick={() => props.onGenerate(props.formData)}
              disabled={props.isGenerating}>
              Regenerate
            </Button>
            <Button variant="secondary" size="sm" leftIcon={<Sliders size={14} />}
              onClick={() => setShowTweakPanel(!showTweakPanel)}
              disabled={props.isGenerating}>
              Tweak &amp; Regenerate
            </Button>
          </div>
          {showTweakPanel && (
            <div className="bg-slate-900/60 border border-slate-700 rounded-2xl p-6 space-y-4 animate-in slide-in-from-top-2 duration-300">
              <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Generation Tweaks</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest">
                    <span className="text-slate-400">Style Strength Offset</span>
                    <span className="text-accent font-mono">{tweakStyleStrength > 0 ? '+' : ''}{tweakStyleStrength}</span>
                  </div>
                  <input type="range" min="-15" max="15" value={tweakStyleStrength}
                    onChange={(e) => setTweakStyleStrength(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-accent" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest">
                    <span className="text-slate-400">Weirdness Offset</span>
                    <span className="text-red-400 font-mono">{tweakWeirdness > 0 ? '+' : ''}{tweakWeirdness}</span>
                  </div>
                  <input type="range" min="-15" max="15" value={tweakWeirdness}
                    onChange={(e) => setTweakWeirdness(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-red-500" />
                </div>
              </div>
              <div className="flex justify-center">
                <Button variant="primary" size="sm" leftIcon={<RefreshCw size={14} />}
                  onClick={() => {
                    const tweaked = {
                      ...props.formData,
                      styleStrength: Math.min(100, Math.max(0, props.formData.styleStrength + tweakStyleStrength)),
                      weirdness: Math.min(100, Math.max(0, props.formData.weirdness + tweakWeirdness)),
                    };
                    props.onGenerate(tweaked);
                    setShowTweakPanel(false);
                  }}
                  disabled={props.isGenerating}>
                  Generate with Tweaks
                </Button>
              </div>
            </div>
          )}

          {!props.activeChain && props.sunoCookie && (
            <div className="flex justify-center">
              <Button
                variant="primary"
                size="sm"
                onClick={props.onStartVariationChain}
                leftIcon={<GitBranch size={14} />}
                className="!text-[11px] !font-black !uppercase !tracking-widest"
              >
                Start Variation Chain
              </Button>
            </div>
          )}

          {props.activeChain && (
            <VariationChainPanel
              chain={props.activeChain}
              tree={props.getTree()}
              onSpawn={props.onSpawnVariations}
              onRate={props.onRateVariationNode}
              onTriggerGeneration={props.onTriggerNodeGen}
              onDelete={props.onDeleteChain}
              isLoading={props.isChainLoading}
            />
          )}
        </div>
      )}
    </div>
  );
};
